// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  DownloadIcon,
  PlusIcon,
  TrashIcon,
  ViewFileIcon,
  WarningIcon,
} from "src/assets/icons";
import Walkthrough from "../Walkthrough/Walkthrough";
import {
  MockButton,
  MockDataHubTabs,
  MockDialog,
  MockIconButton,
  MockNavbar,
  MockPage,
  MockProblemBanner,
  MockSelect,
  MockSnack,
  MockTile,
  mockCardBackground,
} from "../GuideMocks/PortalMocks";
import {
  MockConnectionsHeader,
  MockHubSection,
  MockOutboxPanel,
  MockProblemsBox,
  MockSystemCard,
} from "../GuideMocks/ConnectionMocks";
import { GUIDE_NAMESPACE } from "../GuideText/GuideText";

// Utils
import { WalkthroughStep, guideTarget } from "../Walkthrough/walkthroughState";

// Styles
import {
  getRequiredErrorColor,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

const PAC_SUBTITLE = "DICOM · 10.0.0.20 : 104 · AE FORUM";

const OUTBOX_STEPS: Array<WalkthroughStep> = [
  {
    screen: "c",
    target: "failed",
    text: "A red number under <b>Failed</b> means some reports could not be delivered.",
  },
  { target: "view", text: "Click <b>View</b>." },
  {
    screen: "d",
    target: "note",
    text: "Failed reports retry by themselves for about 20 hours.",
  },
  {
    target: "item",
    text: "Each failed report says when it was last tried and why it failed.",
  },
  { target: "retry", text: "Once the cause is fixed, click <b>Retry</b>." },
  {
    show: ["snack"],
    target: "snack",
    text: "The report goes back in line to be sent.",
  },
];

export const OutboxWalkthrough = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const errorColor = getRequiredErrorColor(theme);

  return (
    <Walkthrough id="outbox" steps={OUTBOX_STEPS}>
      {(view) => (
        <>
          {view.screen === "c" && (
            <Box maxWidth="440px">
              <MockSystemCard
                icon="server"
                name="FORUM"
                subtitle={PAC_SUBTITLE}
                canDelete={false}
                jobs={[
                  {
                    direction: "Out",
                    title: t("Reports"),
                    description: t("RetinaLogik reports stored back"),
                    status: "Failing",
                  },
                ]}
              >
                <MockOutboxPanel
                  pending={0}
                  sent={14}
                  failed={1}
                  viewTarget="view"
                  failedTarget="failed"
                />
              </MockSystemCard>
            </Box>
          )}
          {view.screen === "d" && (
            <Box display="flex" flexDirection="column" gap="12px">
              <MockDialog
                title={t("Report Outbox")}
                footer={
                  <Box marginLeft="auto">
                    <MockButton kind="cancel">{t("Close")}</MockButton>
                  </Box>
                }
              >
                <Typography
                  {...guideTarget("note")}
                  fontSize="12px"
                  color={mutedText}
                >
                  {t(
                    "Failed reports retry by themselves for about 20 hours. After that they wait here until you retry them."
                  )}
                </Typography>
                <Box
                  {...guideTarget("item")}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px",
                    borderRadius: "4px",
                    border: `1px solid ${errorColor}`,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography
                      fontSize="14px"
                      fontWeight={700}
                      color={errorColor}
                    >
                      {t("Failed")}
                    </Typography>
                    <Typography fontSize="12px" color={mutedText}>
                      {`${t("Updated")}: 10/19/2026, 9:42:10 AM · ${t("Attempts")}: 12`}
                    </Typography>
                    <Typography fontSize="12px">
                      {t(
                        "{{address}} refused a report. Check AE {{aeTitle}} may store there.",
                        {
                          address: "FORUM",
                          aeTitle: "EMRHUB",
                        }
                      )}
                    </Typography>
                  </Box>
                  <MockButton kind="confirm" small targetId="retry">
                    {t("Retry")}
                  </MockButton>
                </Box>
              </MockDialog>
              {view.shows("snack") && (
                <MockSnack
                  text={t("Report queued to send again.")}
                  targetId="snack"
                />
              )}
            </Box>
          )}
        </>
      )}
    </Walkthrough>
  );
};

const PROCESSED_STEPS: Array<WalkthroughStep> = [
  {
    screen: "t",
    target: "cp",
    text: "<b>Confirm Pending</b> means the hub needs you to confirm who this exam belongs to.",
  },
  { target: "view", text: "Click the <b>view</b> icon." },
  {
    screen: "v",
    target: "pdf",
    text: "On the left: the exam itself, so you can read the name on it.",
  },
  {
    target: "conf",
    text: "<b>Match Confidence</b>: how sure the system is. Green is 80% or more, yellow 50% or more, red below.",
  },
  {
    target: "grid",
    text: "The details read from the file, next to the patient it was matched to. A red sign marks a difference.",
  },
  { target: "keep", text: "Right patient? Click <b>Keep Matched</b>." },
  {
    target: "create",
    text: "New patient? Click <b>Create from Extracted</b>.",
  },
  {
    target: "other",
    text: "Wrong patient? Search for the right one here, or click <b>Add Patient</b>, then <b>Confirm</b>.",
  },
];

const ExamRow = ({
  name,
  birthdate,
  type,
  status,
  date,
  isPending = false,
}: {
  name: string;
  birthdate: string;
  type: string;
  status: string;
  date: string;
  isPending?: boolean;
}) => {
  const theme = useTheme();
  const { dividerBorder } = getSurfaceTokens(theme);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr 1fr",
          sm: "1.3fr 1fr 0.9fr 1fr 1fr auto",
        },
        gap: "8px",
        padding: "10px",
        alignItems: "center",
        fontSize: "13px",
        borderBottom: dividerBorder,
      }}
    >
      <b>{name}</b>
      <span>{birthdate}</span>
      <span>{type}</span>
      <Box
        component="span"
        {...(isPending ? guideTarget("cp") : {})}
        sx={isPending ? { color: "#E2772E", fontWeight: 700 } : undefined}
      >
        {status}
      </Box>
      <span>{date}</span>
      <Box display="flex">
        <MockIconButton>
          <DownloadIcon />
        </MockIconButton>
        {isPending && (
          <MockIconButton targetId="view">
            <ViewFileIcon />
          </MockIconButton>
        )}
        <MockIconButton>
          <TrashIcon />
        </MockIconButton>
      </Box>
    </Box>
  );
};

export const ProcessedExamsWalkthrough = () => {
  const { t } = useTranslation();
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { mutedText, inputBackground, inputBorder } = getSurfaceTokens(theme);
  const errorColor = getRequiredErrorColor(theme);

  return (
    <Walkthrough id="processed" steps={PROCESSED_STEPS}>
      {(view) => (
        <>
          {view.screen === "t" && (
            <MockPage>
              <MockDataHubTabs current={1} />
              <Box
                sx={{
                  backgroundColor: mockCardBackground(theme),
                  borderRadius: "8px",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  gap="12px"
                  flexWrap="wrap"
                >
                  <Typography fontSize="16px" fontWeight={600}>
                    {t("Processed Exams")}
                  </Typography>
                  <Box
                    sx={{
                      minWidth: "180px",
                      height: "33px",
                      borderRadius: "4px",
                      padding: "0 12px",
                      display: "flex",
                      alignItems: "center",
                      color: mutedText,
                      fontSize: "14px",
                      backgroundColor: inputBackground,
                      border: `1px solid ${inputBorder}`,
                    }}
                  >
                    {t("Search Exams")}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: { xs: "none", sm: "grid" },
                    gridTemplateColumns: "1.3fr 1fr 0.9fr 1fr 1fr auto",
                    gap: "8px",
                    padding: "0 10px",
                    fontSize: "12px",
                    fontWeight: 700,
                    color: mutedText,
                  }}
                >
                  <span>{t("Name")}</span>
                  <span>{t("Birthdate")}</span>
                  <span>{t("Type")}</span>
                  <span>{t("Status")}</span>
                  <span>{t("Import Date")}</span>
                  <Box width="120px" />
                </Box>
                <ExamRow
                  name="Jane Moreau"
                  birthdate="03/14/1958"
                  type={t("Visual Field")}
                  status={t("ConfirmPending")}
                  date="2026-10-19"
                  isPending
                />
                <ExamRow
                  name="Omar Haddad"
                  birthdate="11/02/1971"
                  type="OCT"
                  status={t("ProcessingCompleted")}
                  date="2026-10-19"
                />
                <ExamRow
                  name="Lucie Tremblay"
                  birthdate="07/29/1964"
                  type={t("Fundus")}
                  status={t("ProcessingFailure")}
                  date="2026-10-18"
                />
              </Box>
            </MockPage>
          )}
          {view.screen === "v" && (
            <MockDialog
              title={`Jane Moreau · ${t("Birthdate")}: 03/14/1958`}
              maxWidth="760px"
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "14px",
                }}
              >
                <Box
                  {...guideTarget("pdf")}
                  sx={{
                    backgroundColor: inputBackground,
                    borderRadius: "6px",
                    minHeight: "200px",
                    display: "grid",
                    placeItems: "center",
                    color: mutedText,
                    fontSize: "12px",
                    textAlign: "center",
                    padding: "10px",
                  }}
                >
                  {tg("The exam file (PDF preview)")}
                </Box>
                <Box display="flex" flexDirection="column" gap="10px">
                  <Box display="flex" gap="8px">
                    <MockTile grow label={t("Source")} value="EMR Hub" />
                    <MockTile
                      grow
                      label={t("Exam Type")}
                      value={t("Visual Field")}
                    />
                  </Box>
                  <Box {...guideTarget("conf")}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      fontSize="13px"
                      fontWeight={700}
                    >
                      <span>{t("Match Confidence")}</span>
                      <Box component="span" color="#22BE39">
                        86%
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        height: "8px",
                        borderRadius: "4px",
                        backgroundColor: inputBackground,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: "86%",
                          backgroundColor: "#22BE39",
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    {...guideTarget("grid")}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "90px 1fr 1fr",
                      gap: "6px 10px",
                      fontSize: "12.5px",
                      "& .head": { fontWeight: 700, color: mutedText },
                    }}
                  >
                    <span />
                    <span className="head">{t("Extracted Record")}</span>
                    <span className="head">{t("Matched record")}</span>
                    <span className="head">{t("Name")}</span>
                    <span>Jane Moreau</span>
                    <span>Jane Moreau</span>
                    <span className="head">{t("Birthdate")}</span>
                    <span>03/14/1958</span>
                    <span>03/14/1958</span>
                    <span className="head">{t("Healthcare Id")}</span>
                    <Box component="span" color={mutedText}>
                      {t("Not in file")}
                    </Box>
                    <span>4492 118 302</span>
                    <span className="head">{t("Sex")}</span>
                    <span>F</span>
                    <Box
                      component="span"
                      display="inline-flex"
                      alignItems="center"
                      gap="4px"
                      color={errorColor}
                    >
                      <WarningIcon width="14px" height="14px" />M
                    </Box>
                  </Box>
                  <Box display="flex" gap="8px" flexWrap="wrap">
                    <MockButton kind="cancel" small targetId="create">
                      {t("Create from Extracted")}
                    </MockButton>
                    <MockButton kind="confirm" small targetId="keep">
                      {t("Keep Matched")}
                    </MockButton>
                  </Box>
                  <Box {...guideTarget("other")}>
                    <MockTile
                      label={<b>{t("Choose a Different Patient")}</b>}
                      value={
                        <Box
                          display="flex"
                          flexDirection="column"
                          gap="8px"
                          fontWeight={400}
                        >
                          <MockSelect
                            label=""
                            value=""
                            placeholder={t("Select a value")}
                            filled={false}
                          />
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <MockButton
                              kind="link"
                              small
                              startIcon={
                                <PlusIcon width="16px" height="16px" />
                              }
                            >
                              {t("Add Patient")}
                            </MockButton>
                            <MockButton kind="confirm" small>
                              {t("Confirm")}
                            </MockButton>
                          </Box>
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </MockDialog>
          )}
        </>
      )}
    </Walkthrough>
  );
};

const PROBLEMS_STEPS: Array<WalkthroughStep> = [
  {
    screen: "b",
    target: "banner",
    text: "An orange bar appears at the top of every page when a hub reports a problem.",
  },
  { target: "viewhub", text: "Click <b>View Hub</b>." },
  {
    screen: "p",
    target: "box",
    text: "The red <b>Problems</b> box lists what is wrong.",
  },
  {
    target: "item",
    text: "Each line names the connection, says what is wrong and how to fix it, and when it was last seen.",
  },
  {
    target: "pill",
    text: "The affected job shows <b>Failing</b>. Once fixed, it turns back to <b>Working</b> and the problem disappears by itself.",
  },
];

export const ProblemsWalkthrough = () => {
  const { t } = useTranslation();
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);
  const folder = "\\\\emr01\\worklist";

  return (
    <Walkthrough id="problems" steps={PROBLEMS_STEPS}>
      {(view) => (
        <>
          {view.screen === "b" && (
            <Box>
              <MockNavbar current="Dashboard" />
              <MockProblemBanner
                text={`1 ${t("EMR Problem Reported on {{hub}}.", {
                  hub: tg("Front desk PC"),
                  count: 1,
                })}`}
                bannerTarget="banner"
                viewTarget="viewhub"
              />
              <MockPage attached>
                <Box padding="16px">
                  <Typography fontSize="16px" fontWeight={600}>
                    {tg("Your dashboard")}
                  </Typography>
                </Box>
              </MockPage>
            </Box>
          )}
          {view.screen === "p" && (
            <MockPage>
              <MockConnectionsHeader title={tg("Front desk PC")} />
              <MockProblemsBox
                boxTarget="box"
                itemTarget="item"
                connection={tg("EMR worklist")}
                message={t(
                  "Folder {{path}} not found. Check the path and that the share is online.",
                  {
                    path: folder,
                  }
                )}
                lastSeen="10/19/2026, 9:42 AM"
              />
              <MockHubSection kind="worklist">
                <MockSystemCard
                  icon="folder"
                  name={tg("EMR worklist")}
                  subtitle={folder}
                  jobs={[
                    {
                      direction: "In",
                      title: t("Worklist"),
                      description: `${t("Worklist CSV")} · ${t("Read every {{minutes}} min", { minutes: 30 })} · ${t("today's appointments")}`,
                      status: "Failing",
                      statusTarget: "pill",
                    },
                  ]}
                />
              </MockHubSection>
            </MockPage>
          )}
        </>
      )}
    </Walkthrough>
  );
};
